import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  compose,
  setDisplayName,
} from 'recompose';
import {
  get,
  noop,
  includes,
} from 'lodash';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';

import Serializer from 'helpers/form-serialize';
import { injectIntl } from 'components';
import { setConfirmAction } from 'redux/ui/actions';

import Checkbox from './components/Checkbox';
import SelectField from './components/SelectField';

const styles = () => ({
  labelAsterisk: { color: 'red' },
  groupsSelectItem: { fontSize: '1rem' },
  groupsSelectLabel: { fontSize: '1rem' },
  groupsSelectLabelForUserType: {
    color: 'red',
    fontSize: '1rem',
  },
  groupSelectLabelForError: { color: 'red' },
  groupSelectLocale: {
    width: 200,
  },
  groupSelectRoot: {
    height: 45,
    width: 200,
  },
  icon: {
    display: 'none',
  },
  saveButton: {
    backgroundColor: '#35b561',
    color: 'white',
  },
});

const SmartForm = ({
  actionClass,
  actionsTop,
  cancelButton,
  children,
  classes,
  defaultData,
  fields,
  placeChildrenBottom,
  isOrganization,
  saveButton,
  saveButtonDisable,
  saveConfirm,
  cancelConfirm,
  setConfirmAction,
  formatMessage,
  onCancel,
  onSave,
  onUpdate,
}) => {
  const actions = top => (
    <div className={`actions ${actionClass} ${top ? 'top' : 'bottom'}`}>
      {!!cancelButton && (
        <Button
          onClick={() => {
            if (cancelConfirm) {
              setConfirmAction({
                action: onCancel,
                description: cancelConfirm,
              });
            } else {
              onCancel();
            }
          }}
          variant="text"
        >
          {cancelButton}
        </Button>
      )}
      <Button
        classes={{ root: classes.saveButton }}
        disabled={saveButtonDisable}
        type="submit"
        variant="contained"
      >
        {saveButton || formatMessage('Save')}
      </Button>
    </div>
  );
  return (
    <form
      autoComplete="none"
      className="smart-form"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const data = Serializer.serialize(e.target, { hash: true, empty: true });
        if (saveConfirm) {
          if (includes(saveConfirm, 'business account with a user')) {
            if (isOrganization) {
              const businessType = data.currentRoleId[0] === '5' ? 'Consumer' : data.currentRoleId[0] === '2' ? 'Buyer' : (data.currentRoleId[0] === '3' ? 'Distributor' : 'Seller');
              setConfirmAction({
                action: () => onSave(data),
                description: `${businessType !== 'Consumer' ? saveConfirm : 'Create an account with a user type:'} ${businessType}`,
              });
            } else {
              onSave(data);
            }
          } else {
            setConfirmAction({
              action: () => onSave(data),
              description: saveConfirm,
            });
          }
        } else {
          onSave(data);
        }
      }}
    >
      {actionsTop && actions(true)}
      <div className="content row">
        {!placeChildrenBottom && children}
        {
          fields.map(({
            defaultValue: originalValue,
            fieldName,
            fullWidth,
            textRight,
            fontSize,
            hidden,
            kind,
            label,
            hasLabel,
            name,
            options,
            readOnly,
            render,
            required,
            type,
            error,
            append,
            userType,
            onClick,
            inputProps,
            onChange = noop,
          }) => {
            let component = null;
            const renderValue = typeof render === 'function' ? render(defaultData) : render;
            const defaultValue = renderValue || get(defaultData, `${fieldName}`) || originalValue;
            const inputName = readOnly ? '' : name;

            // Classes for styling the field container
            const fullWidthClass = fullWidth ? '' : 'col-sm-6';
            const marginClass = (name === 'userInfo' || userType) ? 'margin' : '';
            const textRightClass = textRight ? 'text-right' : ''; // text-right is provided by Bootstrap
            const containerClass = `form-field col-12 ${fullWidthClass} ${marginClass} ${textRightClass}`;
            switch (kind) {
              case 'blank':
                component = (
                  <div style={{
                    marginTop: name === 'userInfo' ? 0 : 20,
                    fontSize: fontSize || 20,
                    color: required ? 'red' : 'black',
                  }}>
                    {label}
                  </div>
                );
                break;
              case 'button':
                component = (
                  <Button
                    onClick={onClick}
                    variant="contained"
                    style={{ marginBottom: 20 }}
                  >
                    {label}
                  </Button>
                );
                break;
              case 'select': {
                const selectField = (
                  <SelectField
                    classes={{
                      item: { root: classes.groupsSelectItem },
                      root: { root: name === 'currentLocale' ?
                        classes.groupSelectLocale : classes.groupSelectRoot },
                      select: {
                        icon: userType ? classes.icon : '',
                        select: userType ? classes.groupSelectLabelForError
                          : classes.groupsSelectLabel,
                      },
                    }}
                    defaultValue={defaultValue}
                    disabled={readOnly}
                    fullWidth
                    label={label}
                    name={inputName}
                    options={options}
                    required={required}
                    onChange={(e) => {
                      onChange(e);
                      onUpdate();
                    }}
                  />
                );
                component = hasLabel ? (
                  <div className="select-field">
                    <span>{label}</span>
                    {selectField}
                  </div>
                ) : selectField;
                break;
              }
              case 'checkbox':
                component = (
                  <Checkbox
                    defaultChecked={defaultValue}
                    disabled={readOnly}
                    fullWidth
                    label={label}
                    name={inputName}
                    options={options}
                    onClick={(e) => {
                      onClick(e);
                      onUpdate();
                    }}
                  />
                );
                break;
              case 'textarea':
                component = (
                  <TextField
                    defaultValue={defaultValue}
                    disabled={readOnly}
                    fullWidth
                    id="textarea"
                    label={label}
                    multiline
                    name={inputName}
                    required={required}
                    rows={5}
                    rowsMax={5}
                    type={type || 'text'}
                    variant="outlined"
                    autoComplete="none"
                    style={{ margin: '10px 0' }}
                    onChange={(e) => {
                      onChange(e.target.value);
                      onUpdate();
                    }}
                  />
                );
                break;
              case 'return':
                component = null;
                break;
              case 'input':
              default:
                component = (
                  <TextField
                    autoComplete="none"
                    defaultValue={defaultValue}
                    disabled={readOnly}
                    fullWidth
                    id={`${label}-textfield`}
                    InputLabelProps={error ? {
                      classes: {
                        root: classes.groupSelectLabelForError,
                      },
                    } : {
                      shrink: true,
                      FormLabelClasses: {
                        asterisk: classes.labelAsterisk,
                      },
                    }}
                    inputProps={{ step: 'any',...inputProps }}
                    label={label}
                    name={inputName}
                    margin="normal"
                    required={required}
                    style={{ marginBottom: 10 }}
                    type={type || 'text'}
                    variant="outlined"
                    onChange={(e) => {
                      onChange(e.target.value);
                      onUpdate();
                    }}
                  />
                );
            }
            return (
              <div
                className={containerClass}
                key={name}
              >
                {!hidden && component}
                {append}
                {(readOnly || !!hidden) && (
                  <input
                    defaultValue={defaultValue}
                    name={name}
                    type="hidden"
                  />
                )}
              </div>
            );
          })
        }
        {placeChildrenBottom && children}
      </div>
      {isOrganization && actions(false)}
    </form>
  );
};

SmartForm.propTypes = {
  actionClass: PropTypes.string,
  actionsTop: PropTypes.bool,
  cancelButton: PropTypes.string,
  children: PropTypes.node,
  classes: PropTypes.shape({}).isRequired,
  defaultData: PropTypes.shape({}),
  fields: PropTypes.arrayOf(PropTypes.shape({
    kind: PropTypes.string,
    label: PropTypes.node,
    name: PropTypes.string,
    type: PropTypes.string,
  })).isRequired,
  isOrganization: PropTypes.bool,
  placeChildrenBottom: PropTypes.bool,
  saveButton: PropTypes.string,
  saveButtonDisable: PropTypes.bool,
  saveConfirm: PropTypes.string,
  cancelConfirm: PropTypes.string,
  formatMessage: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  onSave: PropTypes.func.isRequired,
  onUpdate: PropTypes.func,
  setConfirmAction: PropTypes.func.isRequired,
};

SmartForm.defaultProps = {
  actionClass: '',
  actionsTop: false,
  cancelButton: null,
  children: null,
  defaultData: {},
  placeChildrenBottom: false,
  saveButton: null,
  saveButtonDisable: false,
  saveConfirm: '',
  cancelConfirm: '',
  onCancel: noop,
  onUpdate: noop,
  isOrganization: true,
  isDeactivated: false,
};

const mapDispatchToProps = dispatch => ({
  setConfirmAction: payload => dispatch(setConfirmAction(payload)),
});

export default compose(
  setDisplayName('SmartForm'),
  connect(
    undefined,
    mapDispatchToProps,
  ),
  injectIntl,
  withStyles(styles),
)(SmartForm);

